import { useState, useCallback, useMemo } from "react";
import { exchangeApi, serialNumbersApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { buildCalendarData } from "@/lib/taskCalendarData";

// Encapsulates all Exchange-calendar concerns of the Tasks page:
// state, data fetching, event CRUD and the derived month grid.
export function useTaskCalendar({ tasks, openEditDialog }) {
  const toast = useToast();

  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [exchangeEvents, setExchangeEvents] = useState([]);
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [calendarOwners, setCalendarOwners] = useState([]);
  const [exchangeEmail, setExchangeEmail] = useState("");

  const fetchCalendarData = useCallback(async () => {
    setCalendarLoading(true);
    try {
      try {
        const exchangeRes = await exchangeApi.getAllCalendarEvents(30);
        const events = Array.isArray(exchangeRes.data?.events) ? exchangeRes.data.events : [];
        setExchangeEvents(events);

        const ownEmail = exchangeRes.data?.own_email || "";
        setExchangeEmail(ownEmail);

        const owners = [];
        if (ownEmail) {
          owners.push({ email: ownEmail, name: ownEmail.split("@")[0].replace(".", " ") });
        }
        const sharedCalendars = exchangeRes.data?.shared_calendars || [];
        sharedCalendars.forEach((email) => {
          if (email && email !== ownEmail) {
            owners.push({ email, name: email.split("@")[0].replace(".", " ") });
          }
        });
        setCalendarOwners(owners);
      } catch {
        try {
          const fallbackRes = await exchangeApi.getCalendarEvents(30);
          const events = Array.isArray(fallbackRes.data?.events) ? fallbackRes.data.events : [];
          setExchangeEvents(events);
        } catch {
          setExchangeEvents([]);
        }
      }

      try {
        const serialRes = await serialNumbersApi.getCalendar();
        const serials = Array.isArray(serialRes.data) ? serialRes.data : [];
        setSerialNumbers(serials);
      } catch {
        setSerialNumbers([]);
      }
    } catch (error) {
      console.debug("Kalenderdaten konnten nicht geladen werden:", error);
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  const fetchSharedCalendars = useCallback(async () => {
    try {
      const statusRes = await exchangeApi.getStatus();
      const ownEmail = statusRes.data?.email || "";
      setExchangeEmail(ownEmail);

      const listRes = await exchangeApi.listSharedCalendars();
      const sharedEmails = listRes.data?.shared_calendars || [];

      const owners = [];
      if (ownEmail) {
        const name = ownEmail.split("@")[0].split(".").map((n) => n.charAt(0).toUpperCase() + n.slice(1)).join(" ");
        owners.push({ email: ownEmail, name });
      }
      sharedEmails.forEach((email) => {
        if (email && email !== ownEmail) {
          const name = email.split("@")[0].split(".").map((n) => n.charAt(0).toUpperCase() + n.slice(1)).join(" ");
          owners.push({ email, name });
        }
      });
      setCalendarOwners(owners);
    } catch (error) {
      console.debug("Freigegebene Kalender konnten nicht geladen werden:", error);
    }
  }, []);

  const handleCreateCalendarEvent = async (eventData) => {
    setSavingEvent(true);
    try {
      await exchangeApi.createCalendarEvent(
        eventData.subject,
        eventData.start_time,
        eventData.end_time,
        eventData.body,
        eventData.location,
        eventData.attendee_emails,
        true,
        eventData.recurrence || "none",
        eventData.reminder_minutes || 15,
        eventData.calendar_owner || exchangeEmail
      );
      const ownerName = eventData.calendar_owner
        ? eventData.calendar_owner.split("@")[0].replace(".", " ")
        : "Ihrem";
      toast.success(`Termin in ${ownerName}s Kalender erstellt`);
      setEventDialogOpen(false);
      setSelectedEvent(null);
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen des Termins");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleUpdateCalendarEvent = async (eventData) => {
    if (!selectedEvent?.id) return;
    setSavingEvent(true);
    try {
      await exchangeApi.updateCalendarEvent(selectedEvent.id, {
        subject: eventData.subject,
        start_time: eventData.start_time,
        end_time: eventData.end_time,
        body: eventData.body,
        location: eventData.location,
      });
      toast.success("Termin aktualisiert");
      setEventDialogOpen(false);
      setSelectedEvent(null);
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Aktualisieren des Termins");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteCalendarEvent = async (eventId) => {
    setDeletingEvent(true);
    try {
      await exchangeApi.deleteCalendarEvent(eventId);
      toast.success("Termin gelöscht");
      setEventDialogOpen(false);
      setSelectedEvent(null);
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen des Termins");
    } finally {
      setDeletingEvent(false);
    }
  };

  const handleEventSubmit = (eventData) => {
    if (selectedEvent) {
      handleUpdateCalendarEvent(eventData);
    } else {
      handleCreateCalendarEvent(eventData);
    }
  };

  const handleEventClick = (event) => {
    if (event.type === 'exchange' && event.isOwn !== false) {
      setSelectedEvent(event);
      setEventDialogOpen(true);
      return;
    }
    if (event.type === 'task') {
      const full = tasks.find((t) => t.id === event.id);
      if (full) openEditDialog(full);
    }
  };

  const openCreateEventDialog = () => {
    setSelectedEvent(null);
    setEventDialogOpen(true);
  };

  const calendarData = useMemo(
    () => buildCalendarData({ calendarMonth, tasks, exchangeEvents, serialNumbers }),
    [calendarMonth, tasks, exchangeEvents, serialNumbers]
  );

  return {
    calendarMonth,
    setCalendarMonth,
    calendarLoading,
    calendarData,
    eventDialogOpen,
    setEventDialogOpen,
    savingEvent,
    deletingEvent,
    selectedEvent,
    setSelectedEvent,
    calendarOwners,
    exchangeEmail,
    fetchCalendarData,
    fetchSharedCalendars,
    handleEventSubmit,
    handleDeleteCalendarEvent,
    handleEventClick,
    openCreateEventDialog,
  };
}
